"use client"

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react"

import ProductPreview from "@modules/products/components/product-preview"
import { API_BASE_URL } from "@lib/config"
import { SortOptions } from "@modules/store/components/refinement-list/sort-products"
import { readJsonResponse } from "@lib/http/read-json-response"

type Region = Awaited<
  ReturnType<(typeof import("@lib/data/regions"))["getRegion"]>
>

type RemoteFetcherConfig = {
  endpoint?: string
  limit: number
  nextPage: number | null
  query?: Record<string, any>
  sortBy?: SortOptions
}

type InfiniteProductGridProps = {
  region: Region
  initialProducts: any[]
  pageSize: number
  initialTotalCount?: number
  remoteFetcher?: RemoteFetcherConfig
  gridClassName?: string
}

const buildQueryParams = (config: RemoteFetcherConfig, page: number) => {
  const params = new URLSearchParams({
    limit: String(config.limit),
    offset: String((page - 1) * config.limit),
  })

  const maybeAppendArray = (name: string, value: any) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (typeof entry !== "undefined" && entry !== null) {
          params.append(name, String(entry))
        }
      })
      return
    }

    if (typeof value !== "undefined" && value !== null) {
      params.set(name, String(value))
    }
  }

  Object.entries(config.query || {}).forEach(([key, value]) => {
    maybeAppendArray(key, value)
  })

  if (config.sortBy) {
    params.set("sort", config.sortBy)
  }

  return params
}

export function InfiniteProductGrid({
  region,
  initialProducts,
  pageSize,
  initialTotalCount,
  remoteFetcher,
  gridClassName,
}: InfiniteProductGridProps) {
  const [items, setItems] = useState(initialProducts)
  const [visibleCount, setVisibleCount] = useState(
    Math.min(initialProducts.length, pageSize)
  )
  const [nextPage, setNextPage] = useState(remoteFetcher?.nextPage ?? null)
  const [totalCountState, setTotalCountState] = useState(
    initialTotalCount ?? initialProducts.length
  )
  const [isPending, startTransition] = useTransition()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const totalCount = totalCountState

  const shouldFetchRemote = Boolean(remoteFetcher) && nextPage !== null
  const hasLocalMore = !remoteFetcher && visibleCount < items.length
  const hasMore = shouldFetchRemote || hasLocalMore

  const triggerLoadMore = useCallback(() => {
    if (isLoading || isPending || !hasMore) {
      return
    }

    if (remoteFetcher && nextPage) {
      setIsLoading(true)
      setError(null)

      startTransition(async () => {
        try {
          const params = buildQueryParams(remoteFetcher, nextPage)
          const queryString = params.toString()
          const endpoint = remoteFetcher.endpoint
            ? remoteFetcher.endpoint.startsWith("http")
              ? remoteFetcher.endpoint
              : `${API_BASE_URL}${
                  remoteFetcher.endpoint.startsWith("/") ? "" : "/"
                }${remoteFetcher.endpoint}`
            : `${API_BASE_URL}/public/products`
          const requestUrl = `${endpoint}${
            endpoint.includes("?") ? "&" : "?"
          }${queryString}`

          const response = await fetch(requestUrl, {
            headers: {
              "Content-Type": "application/json",
            },
          })

          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`)
          }

          const data: any = await readJsonResponse(response, {
            context: "product grid",
          })

          const newProducts = Array.isArray(data?.products)
            ? data.products
            : Array.isArray(data)
              ? data
              : []
          const productsLoadedSoFar =
            (nextPage - 1) * remoteFetcher.limit + newProducts.length
          const updatedTotal =
            typeof data?.count === "number" && Number.isFinite(data.count)
              ? data.count
              : totalCount
          const calculatedNextPage =
            productsLoadedSoFar < updatedTotal ? nextPage + 1 : null

          setItems((prev) => [...prev, ...newProducts])
          setTotalCountState(updatedTotal)
          setNextPage(calculatedNextPage)
        } catch (err) {
          console.error("Failed to load more products", err)
          setError("No pudimos cargar más productos. Intenta nuevamente.")
          setNextPage(null)
        } finally {
          setIsLoading(false)
        }
      })
      return
    }

    setVisibleCount((prev) => Math.min(prev + pageSize, items.length))
  }, [
    hasMore,
    isLoading,
    isPending,
    items.length,
    nextPage,
    pageSize,
    remoteFetcher,
    startTransition,
    totalCount,
  ])

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isPending) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          triggerLoadMore()
        }
      },
      { rootMargin: "200px 0px" }
    )

    observer.observe(sentinelRef.current)

    return () => observer.disconnect()
  }, [hasMore, isPending, triggerLoadMore])

  const visibleItems = useMemo(() => {
    return remoteFetcher ? items : items.slice(0, visibleCount)
  }, [items, remoteFetcher, visibleCount])

  useEffect(() => {
    setItems(initialProducts)
    setVisibleCount(Math.min(initialProducts.length, pageSize))
    setNextPage(remoteFetcher?.nextPage ?? null)
    setTotalCountState(initialTotalCount ?? initialProducts.length)
    setError(null)
    setIsLoading(false)
  }, [initialProducts, initialTotalCount, pageSize, remoteFetcher])

  return (
    <div className="flex flex-col gap-8">
      <ul
        className={
          gridClassName ||
          "grid grid-cols-2 w-full md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-8"
        }
        data-testid="products-list"
      >
        {visibleItems.map((product: any) => {
          const key = product.slug || product._id || product.id
          return (
            <li key={key}>
              <ProductPreview product={product} region={region} />
            </li>
          )
        })}
      </ul>

      <div ref={sentinelRef} aria-hidden className="h-1" />

      {(isLoading || error) && (
        <div className="flex justify-center items-center text-sm text-ui-fg-subtle pb-8">
          {isLoading ? "Cargando más productos..." : error}
        </div>
      )}

      {!hasMore && !isLoading && (
        <div className="text-center text-xs text-ui-fg-muted pb-4">
          Has llegado al final de la lista.
        </div>
      )}
    </div>
  )
}

export default InfiniteProductGrid
